/** /notification_changes/ — notification change history (read-only).
 *
 * Standalone surface (not nested under /notifications/) so the history of
 * DELETED notifications stays reachable. The list endpoint never returns the
 * snapshots — fetch them per selected change via `getChange`.
 */
import type { AIChangeRef } from './aiChanges';
import { apiGet } from './client';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';

export type ChangeAction = 'created' | 'updated' | 'deleted';

export interface ChangeActor {
  id: number;
  email: string;
  name: string;
}

export interface NotificationChange {
  id: number;
  notification: number | null;
  action: ChangeAction;
  created_ts: string;
  actor: ChangeActor | null;
  source: string;
  comment: string;
  correlation_id: string;
  ip: string | null;
  changed_paths: string[];
  tour_operator_id_denorm: number | null;
  tour_operator_code: string;
  notification_name: string;
  event: string;
  /** List only: the AI change set that wrote this row, for the History chip. */
  change_set?: AIChangeRef | null;
}

/** Aggregate snapshot — same shape as NotificationDetail's editable surface. */
export type NotificationSnapshot = {
  id: number;
  notification: Record<string, unknown>;
  delivery_methods: Record<string, unknown>[];
  block_refs: Record<string, unknown>[];
  rules: Record<string, unknown>[];
  advanced_rules: Record<string, unknown>[];
  reactions: Record<string, unknown>[];
  /** Absent in snapshots taken before the auto-reply pass (2026-08-18). */
  auto_replies?: Record<string, unknown> | null;
} | null;

export interface NotificationChangeDetail extends NotificationChange {
  state_before: NotificationSnapshot;
  state_after: NotificationSnapshot;
}

export interface ChangeListParams {
  notification?: number;
  actor?: number;
  source?: string;
  action?: ChangeAction;
  /** One id or a comma list of ids (D1). */
  tour_operator_id?: TenantIdsParam;
  created_from?: string;
  created_to?: string;
  page?: number;
  page_size?: number;
}

export async function listChanges(
  params: ChangeListParams,
): Promise<PaginatedResponse<NotificationChange>> {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') usp.set(key, String(value));
  }
  return apiGet(`/notification_changes/?${usp.toString()}`);
}

export async function getChange(id: number): Promise<NotificationChangeDetail> {
  return apiGet(`/notification_changes/${id}/`);
}
